/**
 * Where the QM Assistant is shown: floating over the page, or docked inside
 * the cockpit of the stage on screen (Peter, 2026-10-14: "stage 4 and 5 have
 * QM assistant built in to the cockpit").
 *
 * There is ONE Assistant, mounted once at App level so a conversation and a
 * running turn survive stage changes. A cockpit never mounts its own copy. It
 * registers a dock entry instead, and the App-level panel renders into the
 * host element of the entry on top of the stack.
 *
 * Why a stack: the Stage 5 quote editor opens OVER Stage 4, which stays
 * mounted underneath. Each cockpit keeps one entry while it is mounted. Its
 * host is its Assistant tab panel while that tab shows, and null otherwise.
 * The top entry decides alone, so a Stage 5 cockpit showing its files tab
 * keeps the Assistant out of the hidden Stage 4 cockpit below it.
 */
import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';

export interface AssistantDockEntry {
    /** The Assistant tab panel to render into, or null when that tab is hidden. */
    host: HTMLElement | null;
    /** Brings this cockpit's Assistant tab into view (expand, select the tab). */
    show: (() => void) | null;
}

interface StackItem extends AssistantDockEntry {
    id: number;
}

let stack: StackItem[] = [];
let nextId = 1;
let activeHost: HTMLElement | null = null;
const listeners = new Set<() => void>();

function publish() {
    const top = stack[stack.length - 1];
    const host = top?.host && top.host.isConnected !== false ? top.host : null;
    if (host === activeHost) return;
    activeHost = host;
    listeners.forEach((listener) => listener());
}

export interface AssistantDockHandle {
    update: (entry: Partial<AssistantDockEntry>) => void;
    release: () => void;
}

/** A cockpit calls this once on mount and releases it on unmount. */
export function registerAssistantDock(entry: AssistantDockEntry): AssistantDockHandle {
    const id = nextId++;
    stack = [...stack, { id, host: entry.host, show: entry.show }];
    publish();
    return {
        update(patch) {
            let changed = false;
            stack = stack.map((item) => {
                if (item.id !== id) return item;
                changed = true;
                return { ...item, ...patch };
            });
            if (changed) publish();
        },
        release() {
            const before = stack.length;
            stack = stack.filter((item) => item.id !== id);
            if (stack.length !== before) publish();
        },
    };
}

/** The element the Assistant renders into now, or null to float. */
export function getAssistantDockHost(): HTMLElement | null {
    return activeHost;
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}

export function useAssistantDockHost(): HTMLElement | null {
    return useSyncExternalStore(subscribe, getAssistantDockHost, () => null);
}

/**
 * A cockpit's dock entry for as long as `enabled` holds. `host` is its
 * Assistant tab panel while that tab shows, else null. `show` selects that
 * tab; it is read when called, so it may change every render.
 */
export function useAssistantDockEntry(enabled: boolean, host: HTMLElement | null, show: () => void) {
    const showRef = useRef(show);
    showRef.current = show;
    const handleRef = useRef<AssistantDockHandle | null>(null);
    useLayoutEffect(() => {
        if (!enabled) return undefined;
        const handle = registerAssistantDock({ host: null, show: () => showRef.current() });
        handleRef.current = handle;
        return () => {
            handle.release();
            if (handleRef.current === handle) handleRef.current = null;
        };
    }, [enabled]);
    useLayoutEffect(() => {
        handleRef.current?.update({ host: enabled ? host : null });
    }, [enabled, host]);
}

/** Asks a docked Assistant to put the cursor in its composer. */
export const ASSISTANT_FOCUS_COMPOSER_EVENT = 'qm-assistant-focus-composer';

/**
 * The menu's Assistant button on a stage with a cockpit: show the cockpit's
 * Assistant tab instead of the floating panel. Returns false when no cockpit
 * can host it, so the caller opens the floating panel as before.
 */
export function requestCockpitAssistant(): boolean {
    const top = stack[stack.length - 1];
    if (!top || typeof top.show !== 'function') return false;
    const alreadyDocked = !!activeHost;
    top.show();
    if (alreadyDocked && typeof window !== 'undefined') {
        window.dispatchEvent(new Event(ASSISTANT_FOCUS_COMPOSER_EVENT));
    }
    return true;
}


// A quote that has just finished generating opens Stage 5 with the
// Assistant in front, so the operator can ask for changes before sending.
// One-shot, and only for an open that follows within a minute.
const JUST_GENERATED_WINDOW_MS = 60_000;
let quoteJustGeneratedAt = 0;

/** Tells an already open Stage 5 that its quote was just regenerated. */
export const QUOTE_JUST_GENERATED_EVENT = 'qm-quote-just-generated';

export function markQuoteJustGenerated(now: number = Date.now()) {
    quoteJustGeneratedAt = now;
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(QUOTE_JUST_GENERATED_EVENT));
}

export function consumeQuoteJustGenerated(now: number = Date.now()): boolean {
    const at = quoteJustGeneratedAt;
    quoteJustGeneratedAt = 0;
    return at > 0 && now - at <= JUST_GENERATED_WINDOW_MS;
}

/** Test seam: forget every entry and flag. */
export function resetAssistantDockForTests() {
    stack = [];
    activeHost = null;
    quoteJustGeneratedAt = 0;
    listeners.forEach((listener) => listener());
}
